// @flow

import qs from 'querystring';
import type {Location as HistoryLocation} from 'history';

const IntegrationBuilders = {
  billing: true,
  checkout: true,
  payments: true,
  terminal: true,
};

export type IntegrationBuilderType = $Keys<typeof IntegrationBuilders>;

/**
 * Represents a location (a URL) that can be displayed. This class provides a
 * simple interface for working with the History API.
 */
export class Location {
  baseUrl: string;

  pathname: string;

  hash: string;

  query: {[name: string]: string};

  state: {[name: string]: any};

  static fromHistoryLocation(loc: HistoryLocation, baseUrl: string): Location {
    const query = loc.search?.length > 0 ? qs.parse(loc.search.substr(1)) : {};
    return new Location({
      baseUrl,
      pathname: loc.pathname,
      hash: loc.hash,
      state: loc.state,
      query,
    });
  }

  constructor({baseUrl, pathname, hash, query, state}: $Shape<Location>) {
    this.baseUrl = baseUrl;
    this.hash = hash;
    this.pathname = pathname;
    this.query = query ?? {};
    this.state = state ?? {};
  }

  derive(args: string | $Shape<Location>): Location {
    let pathname = this.pathname;
    let hash = '';
    let query = {};
    let state;

    if (typeof args === 'object') {
      if (args.pathname) {
        pathname = args.pathname;
      }
      if (args.hash) {
        hash = args.hash;
      }
      if (args.query) {
        query = args.query;
      }
      if (args.state) {
        state = args.state;
      }
    } else {
      const url = new URL(args, this.baseUrl);
      pathname = url.pathname;
      hash = url.hash;
      query = url.search?.length > 0 ? qs.parse(url.search.substr(1)) : {};
    }

    return new Location({
      baseUrl: this.baseUrl,
      pathname,
      hash,
      query,
      state,
    });
  }

  toHistoryLocation(): $Shape<HistoryLocation> {
    return {
      pathname: this.pathname,
      hash: this.hash,
      search: qs.stringify(this.query),
      state: this.state,
    };
  }

  toURL(): URL {
    const url = new URL(this.pathname, this.baseUrl);
    url.hash = this.hash;
    url.search = qs.stringify(this.query);
    return url;
  }

  toString(): string {
    return this.toURL().toString();
  }

  isIntegrationBuilder(): boolean {
    const match =
      this.pathname?.match(/\/docs\/([A-Za-z]+)\/integration-builder/) || [];
    return match.length > 0;
  }

  integrationBuilderType(): ?IntegrationBuilderType {
    const match =
      this.pathname?.match(/\/docs\/([A-Za-z]+)\/integration-builder/) || [];
    if (!match.length) {
      return null;
    }
    return IntegrationBuilders[match[1]] ? match[1] : null;
  }
}
